import { Injectable } from "@angular/core";
import { CustomerService } from "./customer.service";
import { APSPayment, APSResponse, APSSelectionItem, GeneralResponse, OneTimePaymentRequest, OneTimePaymentResponse, PaymentReceiptItem, RemoveAPSItemRequest, V2Client, WebCustomer } from "../models";
import { BehaviorSubject, Observable } from "rxjs";
import 
{
  AddAPSRequestOfBankInfo,
  AddAPSRequestOfCreditCardInfo,
  APSClient,
  APSMaster,
  BankInfo,
  CreditCardInfo,
} from "src/app/models";
import { filter, tap, first } from "rxjs/operators";
import { SharedService } from "./shared.service";


@Injectable({
  providedIn: "root"
})
export class ApsService
{

  private customer: WebCustomer;

  private availableBanksSubject = new BehaviorSubject<APSMaster[]>([]);
  public availableBanks: Observable<APSMaster[]> = this.availableBanksSubject.asObservable();

  private availableCardsSubject = new BehaviorSubject<APSMaster[]>([]);
  public availableCards: Observable<APSMaster[]> = this.availableCardsSubject.asObservable();

  private bankTypesSubject = new BehaviorSubject<APSSelectionItem[]>([]);
  public bankTypes: Observable<APSSelectionItem[]> = this.bankTypesSubject.asObservable();

  private cardTypes: APSSelectionItem[] = [];
  public cardEntryAllowed: boolean = true;
  public apsLoading: boolean = true;
  public isApsAvailable: boolean = false;
  public areCardsAvailable: boolean = false;
  public areBanksAvailable: boolean = false;
  public isAutoPayOn: boolean = false;
  public isMakeAPaymentAvailable: boolean = false;
  public convenienceFee: number;
  public convenienceFeeTax: number;
  public convenienceFeeDescription: string;
  public bankName: string = ""

  private readonly genericCardType: string = "genericcard";

  constructor(
    private apsClient: APSClient,
    private v2Client: V2Client,
    private customerService: CustomerService,
  ) 
  {
    this.customerService.customer
      .pipe(filter(customer => !!customer))
      .subscribe(customer =>
      {
        this.customer = customer;
        this.apsStartup(customer.BranchNo);
        this.getPaymentMethods();
      });
  }

  private getPaymentMethods(): void 
  {
    if (this.customer?.ApsSourceNo === 0) 
    {
      this.isApsAvailable = false
    }
    if (this.isApsAvailable && this.customer)
    {
      this.apsClient.getApsMasterList("C", this.customer.ApsSourceNo)
        .subscribe(paymentMethods => this.populatePaymentMethods(paymentMethods));
      this.checkCardEntry();
    }
  }

  public checkIsMakeAPaymentAvailable()
  {
    if (this.isApsAvailable && this.customer?.ApsSourceNo !== 0 && this.customer?.AcctStatDesc !== 'Legal')
    {
      this.isMakeAPaymentAvailable = true;
    }
  }

  public getChasePage(otp: OneTimePaymentRequest): Observable<string> 
  {
    return this.apsClient.chaseRequestForm(otp);
  }

  public lookupChasePayment(dataLogical: string, uid: string) 
  {
    return this.apsClient.chasePaymentLookup(dataLogical, uid);
  }

  public sendPaymentReceipt(paymentReceiptItem: PaymentReceiptItem)
  {
    return this.apsClient.sendOneTimePaymentEmail(paymentReceiptItem);
  }

  private populatePaymentMethods(paymentMethods: APSMaster[])
  {
    const cards: APSMaster[] = [];
    const banks: APSMaster[] = [];

    paymentMethods.forEach(p =>
    {
      if (p.PymtMethod === "C")
      {
        cards.push(p);
      }
      else
      {
        banks.push(p);
      }
    });

    const sortByPrimaryDesc = (a: APSMaster, b: APSMaster) =>
      (b.Primary ? 1 : 0) - (a.Primary ? 1 : 0);

    cards.sort(sortByPrimaryDesc);
    banks.sort(sortByPrimaryDesc);

    this.availableCardsSubject.next(cards);
    this.availableBanksSubject.next(banks);

    this.checkAutopay();
  }

  public checkCardEntry(): void 
  {
    this.apsClient.isCreditCardEntryAllowed().subscribe(r => 
    {
      this.cardEntryAllowed = r
    });
  }

  public addCreditCard(creditCardInfo: CreditCardInfo): Observable<boolean> 
  {
    const addCreditCardRequest: AddAPSRequestOfCreditCardInfo =
    {
      Item: creditCardInfo,
      CustId: this.customer.CustId,
      SourceNo: this.customer.ApsSourceNo,
      BranchNo: this.customer.BranchNo
    };
    return this.apsClient.addCard(addCreditCardRequest)
      .pipe(tap(_ => this.getPaymentMethods()));
  }

  public addBankAccount(bankInfo: BankInfo): Observable<boolean> 
  {
    const addBankRequest: AddAPSRequestOfBankInfo =
    {
      Item: bankInfo,
      CustId: this.customer.CustId,
      SourceNo: this.customer.ApsSourceNo,
      BranchNo: this.customer.BranchNo
    };
    return this.apsClient.addBank(addBankRequest)
      .pipe(tap(_ => this.getPaymentMethods()));
  }

  public lookupBankName(routingNumber: string): Observable<any>
  {
    return this.apsClient.getBankName(routingNumber)
  }


  public updatePaymentMethod(paymentMethod: APSMaster): Promise<boolean>
  {
    if (SharedService.IsNullOrEmpty(paymentMethod.BranchNo))
    {
      paymentMethod.BranchNo = this.customer.BranchNo;
    }
    return this.apsClient.updateApsMaster(paymentMethod).toPromise()
      .then(response =>
      {
        if (response.Successful)
        {
          this.getPaymentMethods();
        }
        return response.Successful;
      });
  }

  public deletePaymentMethod(paymentMethod: APSMaster): Observable<boolean>
  {
    paymentMethod.BranchNo = this.customer.BranchNo;
    paymentMethod.SourceNo = this.customer.ApsSourceNo;

    return this.apsClient.removeApsItemV2(paymentMethod).pipe(
      tap(r =>
      {
        if (r)
        {
          this.getPaymentMethods();
        }
      })
    );
  }

  public checkAutopay(): void
  {
    if (this.customer.PayMethod == "B" || "C")
    {
      this.isAutoPayOn = true
    }
    if (this.customer.PayMethod == " ")
    {
      this.isAutoPayOn = false;
    }
  }

  /**
   * @description Detects the card type based off the card number.
   * @param cardNumber {string} expecting credit card number.
   * @returns returns the type of credit card.
   */
  public detectCardType(cardNumber: string): string
  {
    const re = {
      AE: /^(34|37)/,
      D: /^(6011)/,
      MC: /^(51|52|53|54|55|2)/,
      V: /^(4)/,
    };

    for (const key in re)
    {
      if (re[key].test(cardNumber))
      {
        return key;
      }
    }
    return this.genericCardType;
  }

  private luhnChecksum(cardNumber: string): number
  {
    const len = cardNumber.length;
    const parity = len % 2;
    let sum = 0;
    for (let i = len - 1; i >= 0; i--)
    {
      // tslint:disable-next-line: radix
      let d = parseInt(cardNumber.charAt(i));
      if (i % 2 === parity)
      {
        d *= 2;
      }
      if (d > 9)
      {
        d -= 9;
      }
      sum += d;
    }
    return sum % 10;
  }

  public isCardTypeAccepted(cardType: string): boolean
  {
    if (cardType === this.genericCardType)
    {
      return true;
    }

    const val = this.cardTypes.find(e => e.Type === cardType);
    return val != null;
  }

  public luhnValidate(cardNumber: string): boolean
  {
    return this.luhnChecksum(cardNumber) === 0;
  }

  public oneTimePayment(otp: OneTimePaymentRequest): Observable<OneTimePaymentResponse[]>
  {
    if (otp.BudgetAmount)
    {
      if (this.customer.ContractBudget)
      {
        otp.APSPayment.ContractPayment = true;
      }
    }
    otp.APSPayment.SourceNo = this.customer.ApsSourceNo;
    otp.APSPayment.Id = this.customer.CustId;
    otp.APSPayment.RunCode = this.customer.RunCode;
    otp.APSPayment.AccountNumber = this.customer.AcctNo;
    otp.APSPayment.BranchNo = this.customer.BranchNo;
    return this.v2Client.oneTimePayment(otp);
  }

  public getConvenienceFee(otp: OneTimePaymentRequest): Observable<APSResponse>
  {
    otp.APSPayment.SourceNo = this.customer.ApsSourceNo;
    otp.APSPayment.Id = this.customer.CustId;
    otp.APSPayment.RunCode = this.customer.RunCode;
    otp.APSPayment.AccountNumber = this.customer.AcctNo;
    otp.APSPayment.BranchNo = this.customer.BranchNo;
    return this.apsClient.calculateConvenienceFee(otp);
  }

  // public writeApsPayment(payment: APSPayment): Observable<APSResponse> {
  //   return this.apsClient.writeApsPayment(payment);
  // }

  public logChasePayment(payment: APSPayment): Observable<GeneralResponse> 
  {
    return this.apsClient.logChasePayment(payment);
  }

  private apsStartup(branchNo: string): void 
  {
    this.apsClient.getApsStartup(branchNo)
      .subscribe(availableTypes => 
      {
        if (availableTypes?.BankTypes.length != 0 || availableTypes?.CardTypes.length != 0) 
        {
          this.isApsAvailable = true;
          this.areBanksAvailable = availableTypes?.BankTypes?.length > 0;
          this.areCardsAvailable = availableTypes?.CardTypes?.length > 0;

          this.bankTypesSubject.next(availableTypes?.BankTypes);
          this.cardTypes = availableTypes?.CardTypes;
          this.apsLoading = false;
          this.checkIsMakeAPaymentAvailable();
          this.getPaymentMethods();
        }
      });
  }

}
